import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import {
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  ModalBody,
  Row,
  Col,
  UncontrolledTooltip,
} from "reactstrap";
import Switch from "react-switch";

//i18n
import { withTranslation } from "react-i18next";

import { showToast } from "../../functions/utils";

// availity-reactstrap-validation
import { AvForm, AvField } from "availity-reactstrap-validation";

import schema from "./CommentaryValidation";
import ButtonLoading from "./ButtonLoading";
import { useDispatch, useSelector } from "react-redux";
import { createCommentary } from "../../../../store/operator/actions";

const CommentaryMessageModal = ({ t, onCloseModalRequest, isOpen, chatId }) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.Operator.commentary);
  const [value, setValue] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [errors, setErrors] = useState({});

  const setClassName = useCallback((er) => {
    return er ? "av-invalid is-invalid form-control" : "form-control";
  }, []);

  const validateForm = useCallback(async (data) => {
    try {
      setErrors({});

      await schema.validate(data, { abortEarly: false });
      return true;
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[`${error.path}`] = {};
          validationErrors[`${error.path}`]["message"] = error.message;
        });
        setErrors(validationErrors);
      }
      return false;
    }
  }, []);

  const onSubmited = useCallback(
    async (data) => {
      const isValid = await validateForm(data);
      if (isValid) {
        setErrors({});

        dispatch(createCommentary(chatId, value, isPublic));

        onCloseModalRequest();
      }
    },
    [validateForm, chatId, value, dispatch, onCloseModalRequest, isPublic]
  );

  useEffect(() => {
    if (status.success) {
      showToast("success", t("Comment successfully saved"));
      status.success = false;
    }

    if (status.error) {
      showToast("error", t("status.error"));
      status.error = false;
    }
  }, [status]);

  const msgLen = useMemo(() => value.length, [value]);

  const handleClickCloseModalRequest = useCallback(() => {
    setErrors({});
    onCloseModalRequest();
  }, [onCloseModalRequest]);

  const onChangeInput = useCallback((e) => {
    setValue(e.target.value);
  }, []);

  const handlePublic = useCallback((checked) => {
    setIsPublic(checked);
  }, []);

  const handleKeyPressCloseModalRequest = useCallback(
    (e) => {
      if (e.key === "Escape") {
        handleClickCloseModalRequest();
      }
    },
    [handleClickCloseModalRequest]
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleClickCloseModalRequest}
        backdrop={"static"}
      >
        <AvForm onValidSubmit={(e, data) => onSubmited(data)}>
          <ModalHeader
            className="modal-header-no-button py-3 noSelect"
            toggle={handleClickCloseModalRequest}
          >
            {t("Commentary")}
          </ModalHeader>
          <ModalBody>
            <AvField
              type="textarea"
              rows={10}
              columns={10}
              name="commentary"
              className={setClassName(errors?.commentary)}
              placeholder={t("Insert your comment")}
              onChange={onChangeInput}
            />
            <div className="invalid-feedback d-block">
              {t(errors?.commentary?.message)}
            </div>
            <Col className="mt-2">
              <Row className="justify-content-between">
                <div>{t("Maximum 500 characters")}</div>
                <div>{msgLen}/500</div>
              </Row>
            </Col>

            <Col className="mt-3">
              <Row className="d-flex align-items-center">
                <div>{t("Make comment public")}</div>
                <Switch
                  id={`chatMessageToggle-${chatId}`}
                  className="chat-event-switch d-flex align-self-center ml-2"
                  onColor="#34c38f"
                  offColor="#74788D"
                  onHandleColor="#222736"
                  offHandleColor="#fff"
                  handleDiameter={12}
                  width={32}
                  height={16}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  checked={isPublic}
                  onChange={(checked) => handlePublic(checked)}
                />
                <div className="ml-2">
                  <i id="makeCommentPublic" className="bx bx-info-circle" />
                </div>

                <UncontrolledTooltip
                  placement="top"
                  target="makeCommentPublic"
                  className="uncontrolledTooltip"
                >
                  {t(
                    "If you do not select the option to communicate with the customer, the comment will only be internal"
                  )}
                </UncontrolledTooltip>
              </Row>
            </Col>
          </ModalBody>

          <ModalFooter className="flex-nowrap border-0">
            <Button
              type="button"
              color="light"
              className="waves-effect btn-light"
              onClick={handleClickCloseModalRequest}
              onKeyPress={handleKeyPressCloseModalRequest}
            >
              {t("Close")}
            </Button>
            <ButtonLoading
              type="submit"
              color="primary"
              className="waves-effect btn-primary"
              loading={status.loading}
            >
              {t("Add Comment")}
            </ButtonLoading>
          </ModalFooter>
        </AvForm>
      </Modal>
    </>
  );
};

export default withTranslation()(React.memo(CommentaryMessageModal));
